import { type ValidationMessageGroup } from "datasets/pages/tables/types/interfaces/ingestion-job";
import { CheckCircle, AlertCircle, XCircle, Loader2 } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Badge } from "~/components/ui/badge";
import { cn } from "~/lib/utils";

export const StepHeader = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <div>
    <h3 className="text-lg font-semibold mb-1">{title}</h3>
    {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
  </div>
);

export type ValidationStatus = "passed" | "warning" | "running" | "failed" | "uploading";

const statusConfig: Record<
  ValidationStatus,
  {
    variant: "success" | "warning" | "default" | "destructive" | "secondary";
    icon: React.ReactNode;
    label: string;
  }
> = {
  passed: {
    variant: "success",
    icon: <CheckCircle className="w-3 h-3" />,
    label: "PASSED",
  },
  warning: {
    variant: "warning",
    icon: <AlertCircle className="w-3 h-3" />,
    label: "WARNING",
  },
  running: {
    variant: "default",
    icon: <Loader2 className="w-3 h-3 animate-spin" />,
    label: "RUNNING",
  },
  failed: {
    variant: "destructive",
    icon: <XCircle className="w-3 h-3" />,
    label: "FAILED",
  },
  uploading: {
    variant: "secondary",
    icon: <Loader2 className="w-3 h-3 animate-spin" />,
    label: "UPLOADING",
  },
};

export const StatusBadge = ({ status }: { status: ValidationStatus }) => {
  const config = statusConfig[status];
  return (
    <Badge variant={config.variant}>
      {config.icon}
      {config.label}
    </Badge>
  );
};

export interface ValidationJobCardProps {
  title: string;
  description: string;
  result: string;
  status: ValidationStatus;
  details?: string;
  messages?: string[];
  messageGroups?: ValidationMessageGroup[];
}

const messageStyles = {
  warning: {
    className: "bg-yellow-50 border-yellow-200 text-yellow-700",
    Icon: AlertCircle,
  },
  failed: {
    className: "bg-red-50 border-red-200 text-red-700",
    Icon: XCircle,
  },
} as const;

export const ValidationJobCard = ({
  title,
  description,
  result,
  status,
  details,
  messages,
  messageGroups,
}: ValidationJobCardProps) => {
  const style = status === "warning" || status === "failed" ? messageStyles[status] : null;

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium">{title}</h4>
        <StatusBadge status={status} />
      </div>
      <p className="text-sm text-gray-600 mb-1">{description}</p>
      <p className="text-sm text-gray-700">{result}</p>
      {details && <p className="text-sm text-gray-600 mt-2">{details}</p>}

      {style && messageGroups && messageGroups.length > 0 && (
        <div className={cn("mt-2 rounded-lg border text-sm", style.className)}>
          <Accordion type="multiple">
            {messageGroups.map((group) => (
              <AccordionItem key={group.reason} value={group.reason} className="border-b-0">
                <AccordionTrigger className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <style.Icon className="w-3.5 h-3.5 shrink-0" />
                    <span>{group.label}</span>
                    <span className="text-xs opacity-70">
                      ({group.columns.length} {group.columns.length === 1 ? "column" : "columns"})
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-3">
                  <p className="text-xs opacity-80 mb-1.5">{group.description}</p>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {group.columns.map((col) => (
                      <li key={col} className="font-mono text-xs">
                        {col}
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}

      {style && !messageGroups?.length && messages && messages.length > 0 && (
        <ul className={cn("mt-2 p-2 rounded-lg border text-sm space-y-0.5", style.className)}>
          {messages.map((msg, idx) => (
            <li key={idx} className="flex items-start gap-1.5">
              <style.Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{msg}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** Skeleton placeholder matching the ValidationJobCard layout */
export const ValidationJobCardSkeleton = () => (
  <div className="border rounded-lg p-4 animate-pulse">
    <div className="flex items-center justify-between mb-2">
      <div className="h-5 w-36 bg-gray-200 rounded" />
      <div className="h-5 w-20 bg-gray-200 rounded-full" />
    </div>
    <div className="h-4 w-56 bg-gray-100 rounded mb-1" />
    <div className="h-4 w-40 bg-gray-100 rounded" />
  </div>
);

/** Named loading card that shows the job title with a running badge while content pulses */
export const ValidationJobCardLoading = ({ title }: { title: string }) => (
  <div className="border rounded-lg p-4">
    <div className="flex items-center justify-between mb-2">
      <h4 className="font-medium">{title}</h4>
      <StatusBadge status="running" />
    </div>
    <div className="space-y-2 animate-pulse">
      <div className="h-4 w-56 bg-gray-100 rounded" />
      <div className="h-4 w-40 bg-gray-100 rounded" />
    </div>
  </div>
);

export const SummaryItem = ({ label, value }: { label: string; value: string | number }) => (
  <div className="flex justify-between">
    <span className="text-gray-600">• {label}:</span>
    <span className="font-medium">{value}</span>
  </div>
);

export const StatCard = ({
  icon,
  label,
  value,
  color = "gray",
}: {
  icon?: React.ReactNode;
  label: string;
  value: string | number;
  color?: "gray" | "blue" | "green" | "red";
}) => {
  const colorClasses = {
    gray: "bg-gray-600",
    blue: "bg-blue-600",
    green: "bg-green-600",
    red: "bg-red-600",
  };

  return (
    <div className="flex flex-col gap-1 justify-between p-4 border rounded-lg">
      <div className="flex gap-2">
        <div className="py-0.5">
          {icon || <div className={`w-4 h-4 ${colorClasses[color]} rounded aspect-square`}></div>}
        </div>
        <span className="text-sm font-medium text-gray-600">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
};
